if (!customElements.get('l20-third-banner')) {
  class L20ThirdBanner extends HTMLElement {
    connectedCallback() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.remove('l20-third-banner--reveal-pending');
        return;
      }

      this.classList.add('l20-third-banner--reveal-pending');
      this.initializationAttempts = 0;
      this.initializeWhenReady();
    }

    disconnectedCallback() {
      if (this.initializationFrame) window.cancelAnimationFrame(this.initializationFrame);
      this.scrollTrigger?.kill();
      this.timeline?.kill();
      this.animationContext?.revert();
      this.classList.remove('l20-third-banner--animation-ready');
      this.classList.remove('l20-third-banner--reveal-pending');
    }

    initializeWhenReady() {
      if (window.gsap && window.ScrollTrigger) {
        this.initializeAnimation();
        return;
      }

      this.initializationAttempts += 1;
      if (this.initializationAttempts > 180 || !this.isConnected) {
        this.classList.remove('l20-third-banner--reveal-pending');
        return;
      }

      this.initializationFrame = window.requestAnimationFrame(() => this.initializeWhenReady());
    }

    initializeAnimation() {
      const { gsap, ScrollTrigger } = window;

      if (!gsap || !ScrollTrigger) return;

      gsap.registerPlugin(ScrollTrigger);

      const overlay = this.querySelector('.l20-third-banner__overlay');
      const media = this.querySelector('.l20-third-banner__media');
      const title = this.querySelector('.l20-third-banner__title');
      const features = gsap.utils.toArray(this.querySelectorAll('.l20-third-banner__feature'));
      const description = this.querySelector('.l20-third-banner__description');
      const priceLink = this.querySelector('.l20-third-banner__price-link');
      const animatedElements = [overlay, title, ...features, description, priceLink].filter(Boolean);

      if (!animatedElements.length) {
        this.classList.remove('l20-third-banner--reveal-pending');
        return;
      }

      this.classList.add('l20-third-banner--animation-ready');

      try {
        this.animationContext = gsap.context(() => {
          this.timeline = gsap.timeline({
            paused: true,
            defaults: { ease: 'power3.out' },
          });

          if (overlay) gsap.set(overlay, { autoAlpha: 0 });
          if (title) gsap.set(title, { autoAlpha: 0, y: 34 });
          if (features.length) gsap.set(features, { autoAlpha: 0, y: 26 });
          if (description) gsap.set(description, { autoAlpha: 0, y: 22 });
          if (priceLink) gsap.set(priceLink, { autoAlpha: 0, y: 18 });
          this.classList.remove('l20-third-banner--reveal-pending');

          if (overlay) {
            this.timeline.fromTo(overlay, {
              autoAlpha: 0,
            }, {
              autoAlpha: 1,
              duration: 0.9,
              immediateRender: false,
            }, 0);
          }

          if (title) {
            this.timeline.fromTo(title, {
              autoAlpha: 0,
              y: 36,
            }, {
              autoAlpha: 1,
              y: 0,
              duration: 0.95,
              immediateRender: false,
            }, 0.22);
          }

          if (features.length) {
            this.timeline.fromTo(features, {
              autoAlpha: 0,
              y: 28,
            }, {
              autoAlpha: 1,
              y: 0,
              duration: 0.85,
              stagger: 0.07,
              immediateRender: false,
            }, 0.48);
          }

          if (description) {
            this.timeline.fromTo(description, {
              autoAlpha: 0,
              y: 22,
            }, {
              autoAlpha: 1,
              y: 0,
              duration: 0.85,
              immediateRender: false,
            }, 0.86);
          }

          if (priceLink) {
            this.timeline.fromTo(priceLink, {
              autoAlpha: 0,
              y: 18,
            }, {
              autoAlpha: 1,
              y: 0,
              duration: 0.8,
              immediateRender: false,
            }, 1.08);
          }

          this.scrollTrigger = ScrollTrigger.create({
            id: `l20-third-banner-${this.id}`,
            trigger: media || this,
            start: 'center center',
            end: 'bottom top',
            invalidateOnRefresh: true,
            animation: this.timeline,
            toggleActions: 'play none none reset',
          });
        }, this);
      } catch (error) {
        this.classList.remove('l20-third-banner--animation-ready');
        this.classList.remove('l20-third-banner--reveal-pending');
        gsap.set(animatedElements, { clearProps: 'opacity,visibility,transform' });
        console.warn('L20 third banner animation could not initialize.', error);
        return;
      }

      window.requestAnimationFrame(() => {
        this.scrollTrigger?.refresh();
      });
    }
  }

  customElements.define('l20-third-banner', L20ThirdBanner);
}
